import React, { ErrorInfo, ReactNode, Suspense } from 'react'
import { PageError, ReportStatus } from '../pages_v2/page-error'
import fetchAPI from '../../lib/utils/fetch-api'
import {
  isChunkLoadError,
} from './chunk-load-error'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  reportStatus: ReportStatus
  reportSending: boolean
  isChunkLoadError: boolean
}

async function sendClientErrorReport(error: Error, errorInfo: ErrorInfo): Promise<boolean> {
  if (typeof window === 'undefined') return false

  try {
    const result = await fetchAPI(
      '/api/client-errors',
      {
        method: 'POST',
        body: JSON.stringify({
          url: window.location.href,
          pathname: window.location.pathname,
          search: window.location.search || null,
          error_name: error?.name || null,
          error_message: error?.message || 'Unknown error',
          error_stack: error?.stack || null,
          component_stack: errorInfo?.componentStack || null,
          user_agent: navigator.userAgent || null,
          source: 'error_boundary',
        }),
      },
      true,
    )

    return !result?.error && result?.data?.ok === true
  } catch {
    return false
  }
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private lastError: Error | null = null
  private lastErrorInfo: ErrorInfo | null = null

  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      reportStatus: 'idle',
      reportSending: false,
      isChunkLoadError: false,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      reportStatus: 'pending',
      reportSending: true,
      isChunkLoadError: isChunkLoadError(error),
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(error, errorInfo)
    this.lastError = error
    this.lastErrorInfo = errorInfo
    void this.sendReport('auto')
  }

  private sendReport = async (mode: 'auto' | 'retry') => {
    const error = this.lastError
    const errorInfo = this.lastErrorInfo
    if (!error || !errorInfo) {
      this.setState({ reportStatus: 'failed', reportSending: false })
      return
    }

    this.setState({ reportSending: true })
    const ok = await sendClientErrorReport(error, errorInfo)
    this.setState({
      reportSending: false,
      reportStatus: ok ? (mode === 'retry' ? 'sent_retry' : 'sent') : 'failed',
    })
  }

  private handleRetryReport = () => {
    void this.sendReport('retry')
  }

  render() {
    const { hasError, reportStatus, reportSending, isChunkLoadError: chunkError } = this.state
    const { children } = this.props

    if (hasError) {
      return (
        <Suspense fallback="">
          <PageError
            reportStatus={reportStatus}
            reportSending={reportSending}
            onRetryReport={this.handleRetryReport}
            isChunkLoadError={chunkError}
          />
        </Suspense>
      )
    }

    return children
  }
}

export default ErrorBoundary
